import React from "react";
import { CheckCircle } from "lucide-react";
import { useLanguage } from '../context/LanguageContext';

const Loan = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      title: "Loan Mechanisation",
      overview: "Overview",
      overviewText: "Loan Mechanisation enables farmers, women, self-help groups, and rural entrepreneurs to access timely and structured credit through cooperative institutions. The system simplifies the loan application, approval, and disbursal process using digital tools and standardised procedures. The goal is to make cooperative credit faster, transparent, and accessible to every eligible citizen.",
      
      benefits: [
        "Improve access to credit for farmers and rural households",
        "Streamline loan processing to reduce delays",
        "Promote transparency and reduce paperwork",
        "Support income-generating activities and livelihood development",
        "Strengthen cooperative credit delivery at the grassroots",
      ],
      
      processTitle: "Loan Mechanisation Process",
      processSteps: [
        {
          step: "Application Submission",
          description: "Farmer or member applies through PACCS, DCCB or cooperative bank counters. Digital application options available in select societies.",
        },
        {
          step: "Eligibility Verification",
          description: "Land records, crop details, SHG membership, and repayment history verified.",
        },
        {
          step: "Loan Appraisal",
          description: "Society/bank verifies the requirement, credit limit, and repayment capacity.",
        },
        {
          step: "Approval and Sanction",
          description: "Loan is sanctioned based on norms. Member receives sanction intimation.",
        },
        {
          step: "Disbursal",
          description: "Amount transferred directly to the beneficiary's bank account.",
        },
        {
          step: "Monitoring and Renewal",
          description: "Repayment tracked through MIS. Renewal/cycle support provided for recurring loans like KCC.",
        },
      ],
      
      keyStatsTitle: "Key Stats",
      keyStats: [
        { label: "Total Loans Disbursed", value: "X crore" },
        { label: "Farmers Supported", value: "X lakh" },
        { label: "Women Beneficiaries", value: "X lakh" },
        { label: "Automated Loans Processed", value: "X" },
      ],
      
      features: [
        "Simplified digital application",
        "Faster verification and approval",
        "Direct benefit transfer to eligible accounts",
        "Standardised interest rates under cooperative norms",
        "Timely renewal reminders and loan cycle management"
      ]
    },
    ta: {
      title: "கடன் இயந்திரமயமாக்கல்",
      overview: "கண்ணோட்டம்",
      overviewText: "கடன் இயந்திரமயமாக்கல், விவசாயிகள், பெண்கள், சுய உதவிக் குழுக்கள் மற்றும் கிராமப்புற தொழில்முனைவோருக்கு கூட்டுறவு நிறுவனங்கள் மூலம் சரியான நேரத்தில் மற்றும் கட்டமைக்கப்பட்ட கடனை அணுக உதவுகிறது. இந்த அமைப்பு டிஜிட்டல் கருவிகள் மற்றும் தரப்படுத்தப்பட்ட நடைமுறைகளைப் பயன்படுத்தி கடன் விண்ணப்பம், ஒப்புதல் மற்றும் வழங்கல் செயல்முறையை எளிதாக்குகிறது. கூட்டுறவு கடனை விரைவானதாகவும், வெளிப்படையானதாகவும், தகுதியான ஒவ்வொரு குடிமகனுக்கும் அணுகக்கூடியதாகவும் ஆக்குவதே இலக்கு.",
      
      benefits: [
        "விவசாயிகள் மற்றும் கிராமப்புற குடும்பங்களுக்கு கடன் அணுகலை மேம்படுத்துதல்",
        "தாமதங்களைக் குறைக்க கடன் செயலாக்கத்தை ஒழுங்குபடுத்துதல்",
        "வெளிப்படைத்தன்மையை ஊக்குவித்தல் மற்றும் காகித வேலைகளைக் குறைத்தல்",
        "வருமானம் ஈட்டும் செயல்பாடுகள் மற்றும் வாழ்வாதார வளர்ச்சியை ஆதரித்தல்",
        "அடிமட்ட மட்டத்தில் கூட்டுறவு கடன் வழங்கலை வலுப்படுத்துதல்",
      ],
      
      processTitle: "கடன் இயந்திரமயமாக்கல் செயல்முறை",
      processSteps: [
        {
          step: "விண்ணப்ப சமர்ப்பிப்பு",
          description: "விவசாயி அல்லது உறுப்பினர் PACCS, DCCB அல்லது கூட்டுறவு வங்கி கவுண்டர்கள் மூலம் விண்ணப்பிக்கிறார். தேர்ந்தெடுக்கப்பட்ட சங்கங்களில் டிஜிட்டல் விண்ணப்ப விருப்பங்கள் கிடைக்கின்றன.",
        },
        {
          step: "தகுதி சரிபார்ப்பு",
          description: "நில பதிவுகள், பயிர் விவரங்கள், SHG உறுப்பினர் மற்றும் திருப்பிச் செலுத்தும் வரலாறு சரிபார்க்கப்படுகிறது.",
        },
        {
          step: "கடன் மதிப்பீடு",
          description: "சங்கம்/வங்கி தேவை, கடன் வரம்பு மற்றும் திருப்பிச் செலுத்தும் திறனை சரிபார்க்கிறது.",
        },
        {
          step: "ஒப்புதல் மற்றும் அனுமதி",
          description: "விதிமுறைகளின் அடிப்படையில் கடன் அனுமதிக்கப்படுகிறது. உறுப்பினர் அனுமதி அறிவிப்பைப் பெறுகிறார்.",
        },
        {
          step: "வழங்கல்",
          description: "பயனாளியின் வங்கிக் கணக்கில் நேரடியாக தொகை மாற்றப்படுகிறது.",
        },
        {
          step: "கண்காணிப்பு மற்றும் புதுப்பித்தல்",
          description: "MIS மூலம் திருப்பிச் செலுத்துதல் கண்காணிக்கப்படுகிறது. KCC போன்ற மீண்டும் வரும் கடன்களுக்கு புதுப்பித்தல்/சுழற்சி ஆதரவு வழங்கப்படுகிறது.",
        },
      ],
      
      keyStatsTitle: "முக்கிய புள்ளிவிவரங்கள்",
      keyStats: [
        { label: "மொத்த கடன்கள் வழங்கப்பட்டன", value: "X கோடி" },
        { label: "ஆதரிக்கப்பட்ட விவசாயிகள்", value: "X லட்சம்" },
        { label: "பெண் பயனாளிகள்", value: "X லட்சம்" },
        { label: "தானியங்கு கடன்கள் செயலாக்கப்பட்டன", value: "X" },
      ],
      
      features: [
        "எளிமைப்படுத்தப்பட்ட டிஜிட்டல் விண்ணப்பம்",
        "விரைவான சரிபார்ப்பு மற்றும் ஒப்புதல்",
        "தகுதியான கணக்குகளுக்கு நேரடி பலன் பரிமாற்றம்",
        "கூட்டுறவு விதிமுறைகளின் கீழ் தரப்படுத்தப்பட்ட வட்டி விகிதங்கள்",
        "சரியான நேரத்தில் புதுப்பித்தல் நினைவூட்டல்கள் மற்றும் கடன் சுழற்சி மேலாண்மை"
      ]
    }
  };

  const t = content[language];

  return (
    <div className="py-10 px-4 sm:px-6 bg-[#f4f8ff]">
      <div className="w-full bg-white py-10 shadow-sm">
        <h1 className="text-center text-4xl font-bold text-gray-900">
          {t.title}
        </h1>
      </div>

      <div className="max-full mx-auto flex flex-col gap-16">

        {/* Overview */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t.overview}</h2>
          <div className="bg-blue-50 border-l-4 border-blue-600 rounded-lg p-6">
            <p className="text-gray-800 text-lg leading-relaxed">
              {t.overviewText}
            </p>
          </div>
        </section>

        {/* Benefits Boxes Styled Like Image */}
        <section>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {t.benefits.map((benefit, index) => {
              const colors = [
                "bg-teal-500",
                "bg-blue-900",
                "bg-yellow-400",
                "bg-cyan-500",
                "bg-purple-700",
              ];

              return (
                <div
                  key={index}
                  className={`${colors[index]} text-white rounded-2xl p-6 sm:p-8 flex flex-col items-center justify-center gap-4 shadow-lg`}
                >
                  <p className="font-semibold text-center text-base leading-snug">
                    {benefit}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Loan Mechanisation Process */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            {t.processTitle}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {t.processSteps.map((step, index) => (
              <div key={index} className="bg-gradient-to-br from-blue-50 to-white border-l-4 border-blue-600 shadow-md hover:shadow-xl transition-all duration-300 rounded-lg p-4 group">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg group-hover:scale-110 transition-transform">
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-700 transition-colors">
                      {step.step}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{step.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Key Stats */}
        <section className="bg-blue-200 py-10 rounded-xl w-full">
          <h2 className="text-3xl font-bold text-black mb-8 text-center">
            {t.keyStatsTitle}
          </h2>

          <div className="w-full mx-auto px-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {t.keyStats.map((stat, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl shadow-sm p-6 flex items-center gap-4"
                >
                  {/* Icon Circle */}
                  <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 text-xl">
                    {stat.icon}
                  </div>

                  {/* Text */}
                  <div className="text-left">
                    <div className="text-2xl font-bold text-gray-900">
                      {stat.value}
                    </div>
                    <div className="text-sm text-gray-600">
                      {stat.label}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {t.features.map((feature, index) => {
              const colors = [
                "bg-blue-100",
                "bg-white",
                "bg-white",
                "bg-white",
                "bg-white"
              ];
              
              const circleColors = [
                "bg-gray-800",
                "bg-pink-500",
                "bg-green-500",
                "bg-blue-600",
                "bg-purple-600"
              ];

              return (
                <div key={index} className={`${colors[index]} rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow relative`}>
                  <div className="absolute -top-6 left-6">
                    <div className={`w-14 h-14 ${circleColors[index]} rounded-full shadow-lg`}></div>
                  </div>
                  <div className="mt-8">
                    <h3 className="text-lg font-bold text-gray-900 mb-3">
                      {feature}
                    </h3>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Loan;